import { Navigate, Outlet, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/stores/appStore";
import { routes } from "@/routes";

/**
 * Route guard that verifies the authenticated user is a member of
 * the organisation specified in the URL params.
 *
 * Defence-in-depth: the database layer also enforces membership,
 * but this prevents unnecessary API calls for non-members.
 *
 * On direct URL navigation the organisation query may still be in-flight
 * (started by ProtectedLayout). We wait for it to settle before deciding
 * whether to redirect, avoiding a false-negative redirect to dashboard.
 */
export function OrgMembershipGuard() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const userOrganisation = useAppStore((s) => s.organisation);
  const queryClient = useQueryClient();

  const queryState = queryClient.getQueryState([
    "user-organisation-membership",
  ]);
  const isLoading =
    !queryState ||
    queryState.status === "pending" ||
    queryState.fetchStatus === "fetching";

  if (!organisationId) {
    return <Navigate to={routes.dashboard()} replace />;
  }

  // Wait for the organisation query to finish before checking membership
  if (isLoading && !userOrganisation) {
    return null;
  }

  if (
    !userOrganisation ||
    userOrganisation.id.toLowerCase() !== organisationId.toLowerCase()
  ) {
    return <Navigate to={routes.dashboard()} replace />;
  }

  return <Outlet />;
}
